import type { ActivityRow, UserDimRow } from "./sql";
import type { FeatureKey } from "./definitions";

export type Entity = "user" | "company";

// Rolling 12 weeks of week_starts (most-recent week last). Each is a Monday
// because we use date_trunc('week', ts) in SQL (Postgres week starts Monday).
export function recentWeekStarts(now: Date = new Date(), count = 12): string[] {
  const monday = mondayOf(now);
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(monday);
    d.setUTCDate(d.getUTCDate() - i * 7);
    out.push(toIso(d));
  }
  return out;
}

function mondayOf(d: Date): Date {
  const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = utc.getUTCDay() || 7;
  if (dow !== 1) utc.setUTCDate(utc.getUTCDate() - (dow - 1));
  return utc;
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// Keep all activity for users present in the filtered dims set, regardless of
// which company the event is stamped to. Strict company matching dropped events
// stamped to a different company_external_id than the user's current role row.
export function joinActivity(
  filteredUsers: UserDimRow[],
  activity: ActivityRow[],
): ActivityRow[] {
  const userIds = new Set<string>();
  for (const u of filteredUsers) userIds.add(u.user_external_id);
  return activity.filter((a) => userIds.has(a.user_external_id));
}

// Weekly series — count distinct (user|company) keys per week_start.
export function weeklySeries(
  activity: ActivityRow[],
  entity: Entity,
  feature: FeatureKey | "overall",
  weekStarts: string[],
): { week_start: string; value: number }[] {
  const keyer = entity === "user"
    ? (a: ActivityRow) => a.user_external_id
    : (a: ActivityRow) => a.company_external_id ?? "";
  const buckets = new Map<string, Set<string>>();
  for (const w of weekStarts) buckets.set(w, new Set());
  for (const a of activity) {
    if (feature !== "overall" && a.feature !== feature) continue;
    const set = buckets.get(a.week_start);
    if (!set) continue;
    const k = keyer(a);
    if (!k) continue;
    set.add(k);
  }
  return weekStarts.map((w) => ({ week_start: w, value: buckets.get(w)?.size ?? 0 }));
}

// Per-company % seats active for a given week. Output averages
// (active_users / company_active_user_count) across companies that had any
// activity that week.
export function seatPctActiveForWeek(
  filteredUsers: UserDimRow[],
  weekActivity: ActivityRow[],
): { pct: number; activeCompanies: number } {
  const sizeByCompany = new Map<string, number>();
  for (const u of filteredUsers) {
    if (!u.company_external_id) continue;
    if (!sizeByCompany.has(u.company_external_id)) {
      sizeByCompany.set(u.company_external_id, u.marloo_active_user_count || 0);
    }
  }
  const activeUsersByCompany = new Map<string, Set<string>>();
  for (const a of weekActivity) {
    if (!a.company_external_id) continue;
    if (!sizeByCompany.has(a.company_external_id)) continue;
    let set = activeUsersByCompany.get(a.company_external_id);
    if (!set) {
      set = new Set();
      activeUsersByCompany.set(a.company_external_id, set);
    }
    set.add(a.user_external_id);
  }
  let totalRatio = 0;
  let n = 0;
  for (const [company, users] of activeUsersByCompany) {
    const size = sizeByCompany.get(company) ?? 0;
    if (size <= 0) continue;
    totalRatio += users.size / size;
    n += 1;
  }
  return { pct: n === 0 ? 0 : (totalRatio / n) * 100, activeCompanies: n };
}

// Distinct count by entity over an arbitrary slice of activity rows.
export function distinctEntities(rows: ActivityRow[], entity: Entity): number {
  const keyer = entity === "user"
    ? (a: ActivityRow) => a.user_external_id
    : (a: ActivityRow) => a.company_external_id ?? "";
  const seen = new Set<string>();
  for (const a of rows) {
    const k = keyer(a);
    if (k) seen.add(k);
  }
  return seen.size;
}

// Filter activity rows down to a specific week_start.
export function activityInWeek(activity: ActivityRow[], weekStart: string): ActivityRow[] {
  return activity.filter((a) => a.week_start === weekStart);
}

// Bucket a company-size count into a label matching FiltersBar buckets.
export function companySizeBucket(n: number): string {
  if (n <= 1) return "1";
  if (n <= 10) return "2-10";
  if (n <= 50) return "11-50";
  return "50+";
}

const SIZE_LABEL: Record<string, string> = {
  "1": "Solo",
  "2-10": "2-10",
  "11-50": "11-50",
  "50+": "50+",
};
export function companySizeLabel(bucket: string): string {
  return SIZE_LABEL[bucket] ?? bucket;
}
